import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDownToLine, Lock, Save } from 'lucide-react';

import { attendanceApi } from '../../services/api';
import type { GradeSheet, GradeSheetStudent, HomeworkSheetRecord } from '../../types';
import { cn } from '../../utils/cn';
import {
    applyToColumn,
    EMPTY_HOMEWORK,
    HOMEWORK_FIELDS,
    homeworkDraftFrom,
    isLockedByDefault,
    readHomeworkDraft,
    type HomeworkDraft,
    type HomeworkField,
} from '../../utils/gradeSheet';
import { Button, EmptyState, LoadingState, useToast } from '../ui';
import { focusNextRow, lockLabel, SheetNotice, useGradeSheet } from './gradeSheetShared';

const FIELD_LABELS: Record<HomeworkField, string> = {
    correctExercises: 'Đúng',
    completedExercises: 'Đã làm',
    assignedExercises: 'Được giao',
};

const cellInput = 'h-9 w-full min-w-0 rounded border border-ink-300 bg-white px-2 text-center text-sm tabular-nums text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-200 disabled:cursor-not-allowed disabled:bg-ink-100 disabled:text-ink-400';

interface HomeworkSheetProps {
    attendanceId: string;
    canEdit: boolean;
    hasUnsavedAttendance?: boolean;
    refreshKey?: number;
    onSaved?: (students: GradeSheetStudent[]) => void;
}

function initialDrafts(sheet: GradeSheet) {
    return Object.fromEntries(sheet.students.map(student => [student.id, homeworkDraftFrom(student.homework)])) as Record<string, HomeworkDraft>;
}

export function HomeworkSheet({ attendanceId, canEdit, hasUnsavedAttendance, refreshKey, onSaved }: HomeworkSheetProps) {
    const { showToast } = useToast();
    const [drafts, setDrafts] = useState<Record<string, HomeworkDraft>>({});
    const [fill, setFill] = useState<HomeworkDraft>({ ...EMPTY_HOMEWORK });
    const [saving, setSaving] = useState(false);
    const [dirty, setDirtyState] = useState(false);
    const dirtyRef = useRef(false);
    const setDirty = (value: boolean) => {
        dirtyRef.current = value;
        setDirtyState(value);
    };

    const hasHomework = useCallback((student: GradeSheetStudent) => Boolean(student.homework), []);
    const { sheet, setSheet, loading, unlocked, setUnlocked } = useGradeSheet(attendanceId, hasHomework, refreshKey);

    useEffect(() => {
        if (!sheet) return;
        // Đang nhập dở thì giữ số đã nhập; học sinh mới (ví dụ học bù) nhận ô trống.
        setDrafts(current => (dirtyRef.current ? { ...initialDrafts(sheet), ...current } : initialDrafts(sheet)));
    }, [sheet]);

    const results = useMemo(() => Object.fromEntries(
        Object.entries(drafts).map(([id, draft]) => [id, readHomeworkDraft(draft)]),
    ), [drafts]);

    if (loading) return <LoadingState title="Đang tải bài tập về nhà..." />;
    if (!sheet) return <EmptyState title="Không tải được bài tập về nhà" description="Thử đóng và mở lại buổi học." />;
    if (sheet.students.length === 0) {
        return <EmptyState title="Chưa có học sinh" description="Lớp chưa có học sinh nào trong buổi này." />;
    }

    const unlockedIds = sheet.students.filter(student => unlocked.has(student.id)).map(student => student.id);
    const scoredCount = unlockedIds.filter(id => results[id]?.kind === 'ok').length;

    const updateDraft = (studentId: string, field: HomeworkField, value: string) => {
        setDrafts(current => ({ ...current, [studentId]: { ...current[studentId], [field]: value } }));
        setDirty(true);
    };

    const applyFill = (field: HomeworkField) => {
        if (fill[field].trim() === '') return;
        setDrafts(current => applyToColumn(current, unlockedIds, field, fill[field].trim()));
        setDirty(true);
    };

    const setLocked = (student: GradeSheetStudent, locked: boolean) => {
        setUnlocked(current => {
            const next = new Set(current);
            if (locked) next.delete(student.id); else next.add(student.id);
            return next;
        });
        if (locked) setDrafts(current => ({ ...current, [student.id]: { ...EMPTY_HOMEWORK } }));
        setDirty(true);
    };

    const save = async () => {
        const records: HomeworkSheetRecord[] = [];
        for (const student of sheet.students) {
            if (student.status === null) continue;
            const result = unlocked.has(student.id) ? results[student.id] : { kind: 'empty' as const };
            if (result?.kind === 'error') {
                showToast(`${student.name}: ${result.message}`, 'error');
                return;
            }
            if (result?.kind === 'ok') records.push({ studentId: student.id, homework: result.homework });
            else if (student.homework) records.push({ studentId: student.id, homework: null });
        }
        if (records.length === 0) {
            showToast('Chưa có số liệu nào để lưu.', 'info');
            return;
        }
        setSaving(true);
        try {
            const response = await attendanceApi.saveHomework(attendanceId, records);
            setDirty(false);
            setSheet(response.data.data);
            onSaved?.(response.data.data.students);
            showToast('Đã lưu bài tập về nhà.', 'success');
        } catch (error: any) {
            showToast(error?.message || 'Lưu bài tập về nhà thất bại.', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4">
            {hasUnsavedAttendance ? (
                <SheetNotice>Điểm danh đang có thay đổi chưa lưu. Lưu điểm danh trước để danh sách ở đây đúng trạng thái.</SheetNotice>
            ) : null}

            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-ink-600">
                    Đã chấm <span className="font-semibold text-ink-900">{scoredCount}/{unlockedIds.length}</span> học sinh.
                    {canEdit ? ' Điền ở hàng đầu rồi bấm ↓ để áp dụng cho cả lớp.' : ''}
                </p>
                {canEdit ? (
                    <Button onClick={save} loading={saving} disabled={!dirty} icon={<Save className="h-4 w-4" />}>
                        Lưu bài tập
                    </Button>
                ) : null}
            </div>

            <div className="overflow-x-auto rounded-lg border border-ink-200">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-ink-200 bg-ink-50 text-left text-xs font-medium text-ink-600">
                            <th scope="col" className="px-3 py-2">Học sinh</th>
                            {HOMEWORK_FIELDS.map(field => (
                                <th key={field} scope="col" className="w-[3.75rem] px-1 py-2 text-center sm:w-28 sm:px-2">{FIELD_LABELS[field]}</th>
                            ))}
                        </tr>
                        {canEdit ? (
                            <tr className="border-b border-ink-200 bg-academy-50/40">
                                <td className="px-3 py-2" />
                                {HOMEWORK_FIELDS.map(field => (
                                    <td key={field} className="px-1 py-2 sm:px-2">
                                        <div className="flex flex-col items-center gap-1 sm:flex-row">
                                            <input
                                                aria-label={`Giá trị chung cho cột ${FIELD_LABELS[field]}`}
                                                inputMode="decimal"
                                                value={fill[field]}
                                                onChange={event => setFill(current => ({ ...current, [field]: event.target.value }))}
                                                onKeyDown={event => { if (event.key === 'Enter') applyFill(field); }}
                                                className={cellInput}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => applyFill(field)}
                                                disabled={fill[field].trim() === ''}
                                                title="Áp dụng cho cả lớp"
                                                aria-label={`Áp dụng cột ${FIELD_LABELS[field]} cho cả lớp`}
                                                className="flex h-7 w-full shrink-0 items-center justify-center rounded text-academy-700 sm:h-9 sm:w-8 hover:bg-academy-100 disabled:cursor-not-allowed disabled:text-ink-300 disabled:hover:bg-transparent"
                                            >
                                                <ArrowDownToLine className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                ))}
                            </tr>
                        ) : null}
                    </thead>
                    <tbody className="divide-y divide-ink-100">
                        {sheet.students.map((student, rowIndex) => {
                            const isUnlocked = unlocked.has(student.id);
                            const result = results[student.id];
                            const error = isUnlocked && result?.kind === 'error' ? result.message : null;
                            return (
                                <tr key={student.id} className={cn(!isUnlocked && 'bg-ink-50/60')}>
                                    <td className="px-3 py-2 align-middle">
                                        <div>
                                            <span className={cn('block font-medium', isUnlocked ? 'text-ink-900' : 'text-ink-500')}>{student.name}</span>
                                            {isUnlocked && isLockedByDefault(student) && student.status !== null ? (
                                                <span className="flex flex-wrap items-center gap-x-1 text-xs text-warning-700">
                                                    {lockLabel(student)} · nhập bù
                                                    {canEdit ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => setLocked(student, true)}
                                                            className="rounded px-1 text-ink-500 underline-offset-2 hover:text-ink-900 hover:underline"
                                                        >
                                                            Khoá lại
                                                        </button>
                                                    ) : null}
                                                </span>
                                            ) : null}
                                        </div>
                                        {error ? <p className="mt-0.5 text-xs text-danger-700">{error}</p> : null}
                                    </td>
                                    {isUnlocked ? HOMEWORK_FIELDS.map(field => (
                                        <td key={field} className="px-1 py-1.5 sm:px-2">
                                            <input
                                                aria-label={`${FIELD_LABELS[field]} – ${student.name}`}
                                                inputMode="decimal"
                                                enterKeyHint="next"
                                                data-sheet-row={rowIndex}
                                                data-sheet-col={field}
                                                value={drafts[student.id]?.[field] ?? ''}
                                                disabled={!canEdit}
                                                onChange={event => updateDraft(student.id, field, event.target.value)}
                                                onKeyDown={event => { if (event.key === 'Enter') focusNextRow(event.currentTarget); }}
                                                className={cn(cellInput, error && 'border-danger-400')}
                                            />
                                        </td>
                                    )) : (
                                        <td colSpan={3} className="px-3 py-2">
                                            <div className="flex flex-wrap items-center justify-center gap-2 text-sm text-ink-500">
                                                <Lock className="h-3.5 w-3.5" />
                                                <span>{lockLabel(student)}</span>
                                                {canEdit && student.status !== null ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => setLocked(student, false)}
                                                        className="rounded px-2 py-1 text-sm font-medium text-academy-700 hover:bg-academy-50"
                                                    >
                                                        Bật lại
                                                    </button>
                                                ) : null}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
